/**
 * OAuth Callback Handler
 * Handles the redirect from OAuth providers after authentication
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/features/auth';
import { useSessionContext } from '@/contexts/SessionContext';
import { authAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import { notifications } from '@/lib/notifications';
import { FileText, Loader2, CheckCircle, XCircle } from 'lucide-react';

type CallbackStatus = 'processing' | 'success' | 'error';

export const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setNeedsCompany } = useAuthStore();
  const { setSession } = useSessionContext();

  const { t } = useTranslation();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const errorReason = searchParams.get('reason');

      // Handle error from OAuth provider
      if (errorReason) {
        logger.error('OAuth error:', errorReason);
        setStatus('error');
        setError(getErrorMessage(errorReason));
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // No token = error
      if (!token) {
        logger.error('OAuth callback missing token');
        setStatus('error');
        setError(t('auth.oauth.missingToken'));
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        // Set the token temporarily to fetch user data
        setSession(token, 'HTTPONLY_COOKIE', 3600, null);

        // Fetch user data
        const userData = await authAPI.me();

        if (!userData) {
          throw new Error(t('auth.login.userDataError'));
        }

        const hasCompany = !!(userData.companyId && userData.companyId !== null);

        logger.log('OAuth login successful:', {
          id: userData.id,
          email: userData.email,
          hasCompany,
        });

        // Update user in auth store
        setUser(userData);
        setNeedsCompany(!hasCompany);

        setStatus('success');
        notifications.success(t('auth.oauth.success'));

        // Redirect after success
        // SUPERADMIN → direct to superadmin dashboard (bypass onboarding)
        let redirectUrl: string;
        if (userData.role === 'SUPERADMIN') {
          redirectUrl = '/superadmin';
        } else if (hasCompany) {
          redirectUrl = '/dashboard';
        } else {
          redirectUrl = '/onboarding';
        }
        setTimeout(() => navigate(redirectUrl, { replace: true }), 1500);
      } catch (err: any) {
        logger.error('OAuth callback error:', err);
        setStatus('error');
        setError(err.message || t('auth.oauth.authError'));
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser, setNeedsCompany, setSession]);

  const getErrorMessage = (reason: string): string => {
    const errorMessages: Record<string, string> = {
      access_denied: t('auth.oauth.errors.accessDenied'),
      missing_params: t('auth.oauth.errors.missingParams'),
      callback_failed: t('auth.oauth.errors.callbackFailed'),
      invalid_state: t('auth.oauth.errors.invalidState'),
      default: t('auth.oauth.errors.default'),
    };
    return errorMessages[reason] || errorMessages.default;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
      <div className="text-center p-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center shadow-lg shadow-lime-500/30">
            <FileText size={32} strokeWidth={2.5} className="text-teal-700" />
          </div>
        </div>

        {/* Status */}
        <div className="space-y-4">
          {status === 'processing' && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-teal-600 animate-spin" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('auth.oauth.processing')}
              </h2>
              <p className="text-gray-500">
                {t('auth.oauth.processingDescription')}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('auth.oauth.success')}
              </h2>
              <p className="text-gray-500">
                {t('auth.oauth.redirectToDashboard')}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 mx-auto text-red-500" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('auth.oauth.errorTitle')}
              </h2>
              <p className="text-red-600">
                {error}
              </p>
              <p className="text-gray-500 text-sm">
                {t('auth.oauth.redirectToLogin')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
